import { useMemo } from 'react';

import { VFlex } from '@/components/base';
import type { InsightExplorerByType } from '@/features/InsightExplorer/types/constants';

import HierarchyNode from './HierarchyNode';
import { hierarchyNodeUtils } from './utils';

export interface HierarchyNodeData {
  id: string;
  path: ValueLabelT[];
  name: string;
  exceptSelf?: boolean;
  children?: HierarchyNodeData[];
  node_type: InsightExplorerByType;
}

interface HierarchyNavigatorProps {
  data: HierarchyNodeData[];
  searchValue: string;
  onNodeSelect: (node: HierarchyNodeData) => void;
  selectedNodeId?: string;
}

const HierarchyNavigator = ({
  data,
  searchValue,
  onNodeSelect,
  selectedNodeId,
}: HierarchyNavigatorProps) => {
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;

    const filterNodes = (nodes: HierarchyNodeData[]): HierarchyNodeData[] => {
      return nodes.reduce((acc: HierarchyNodeData[], node) => {
        const matchesSearch = node.name
          .toLowerCase()
          .includes(searchValue.toLowerCase());
        const filteredChildren = node.children
          ? filterNodes(node.children)
          : [];

        if (matchesSearch || filteredChildren.length > 0) {
          acc.push({
            ...node,
            children:
              filteredChildren.length > 0 ? filteredChildren : node.children,
          });
        }

        return acc;
      }, []);
    };

    return filterNodes(data);
  }, [data, searchValue]);

  return (
    <VFlex g={4}>
      {filteredData.map((node) => {
        const initialOpen =
          node.id === selectedNodeId ||
          hierarchyNodeUtils
            .getAllChildren(node)
            .some((child) => child.id === selectedNodeId);

        return (
          <HierarchyNode
            key={node.id}
            node={node}
            onNodeSelect={onNodeSelect}
            selectedNodeId={selectedNodeId}
            level={0}
            searchValue={searchValue}
            initialOpen={initialOpen}
          />
        );
      })}
    </VFlex>
  );
};

export default HierarchyNavigator;
